import { ChevronLeft, ChevronRight, MoreHorizontal } from "lucide-react";
import { Button } from "@project/components/ui/button";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@project/components/ui/select";
import { cn } from "@project/lib/utils";

export type TablePaginationProps = {
  page: number;
  pageSize: number;
  totalCount: number;
  totalPages: number;
  onPageChange: (page: number) => void;
  onPageSizeChange?: (size: number) => void;
  pageSizeOptions?: number[];
  loading?: boolean;
  className?: string;
};

function getPageNumbers(
  current: number,
  total: number
): (number | "ellipsis")[] {
  if (total <= 7) {
    return Array.from({ length: total }, (_, i) => i + 1);
  }

  const pages: (number | "ellipsis")[] = [1];

  if (current > 3) {
    pages.push("ellipsis");
  }

  const start = Math.max(2, current - 1);
  const end = Math.min(total - 1, current + 1);
  for (let p = start; p <= end; p += 1) {
    pages.push(p);
  }

  if (current < total - 2) {
    pages.push("ellipsis");
  }
  if (total > 1) {
    pages.push(total);
  }

  return pages;
}

export function TablePagination({
  page,
  pageSize,
  totalCount,
  totalPages,
  onPageChange,
  onPageSizeChange,
  pageSizeOptions = [10, 20, 50, 100],
  loading = false,
  className,
}: TablePaginationProps) {
  const safeTotalPages = Math.max(totalPages, 1);
  const start = totalCount === 0 ? 0 : (page - 1) * pageSize + 1;
  const end = Math.min(page * pageSize, totalCount);
  const pages = getPageNumbers(page, safeTotalPages);

  return (
    <div
      className={cn(
        "flex flex-col gap-3 rounded-xl border border-border bg-card px-3 py-3 sm:flex-row sm:items-center sm:justify-between",
        className
      )}
    >
      <div className="text-xs text-muted-foreground">
        {totalCount === 0
          ? "No records found"
          : `Showing ${start}–${end} of ${totalCount} record${totalCount === 1 ? "" : "s"}`}
      </div>

      <div className="flex flex-wrap items-center justify-center gap-2 sm:justify-end">
        {onPageSizeChange && (
          <div className="flex items-center gap-2 text-xs text-muted-foreground">
            <span>Rows</span>
            <Select
              value={String(pageSize)}
              onValueChange={v => onPageSizeChange(Number(v))}
              disabled={loading}
            >
              <SelectTrigger className="bg-muted/40 h-8 w-[72px] rounded-lg border-transparent text-xs">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {pageSizeOptions.map(size => (
                  <SelectItem key={size} value={String(size)}>
                    {size}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        )}

        <div className="flex items-center gap-1">
          <Button
            type="button"
            variant="outline"
            size="sm"
            className="h-8 gap-1 rounded-lg px-2.5"
            disabled={loading || page <= 1}
            onClick={() => onPageChange(page - 1)}
          >
            <ChevronLeft className="h-4 w-4" />
            <span className="hidden sm:inline">Prev</span>
          </Button>

          {pages.map((p, i) =>
            p === "ellipsis" ? (
              <span
                key={`ellipsis-${String(pages[i - 1])}-${String(pages[i + 1])}`}
                className="flex h-8 w-8 items-center justify-center text-muted-foreground"
              >
                <MoreHorizontal className="h-4 w-4" />
              </span>
            ) : (
              <Button
                key={p}
                type="button"
                variant={p === page ? "default" : "ghost"}
                size="sm"
                className="h-8 min-w-8 rounded-lg px-2"
                disabled={loading}
                onClick={() => onPageChange(p)}
              >
                {p}
              </Button>
            )
          )}

          <Button
            type="button"
            variant="outline"
            size="sm"
            className="h-8 gap-1 rounded-lg px-2.5"
            disabled={loading || page >= safeTotalPages}
            onClick={() => onPageChange(page + 1)}
          >
            <span className="hidden sm:inline">Next</span>
            <ChevronRight className="h-4 w-4" />
          </Button>
        </div>
      </div>
    </div>
  );
}
